import { useEffect, useRef, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Loader2, Sparkles, User, Clock, TrendingUp, Info, ChevronDown, ChevronUp, MessageCircle, List, Code, BarChart3, ArrowRight, Copy, Check, Lightbulb, History, Flame, Briefcase, Brain, Search, LineChart, Wand2 } from 'lucide-react';
import { ChatMessage, ResponseFormatType } from '../../stores/aiAssistantStore';
import MessageFeedback from './MessageFeedback';
import { format } from 'date-fns';
import { aiAssistantAPI, SuggestionItem, SuggestionsResponse } from '../../hooks/useAIAssistant';
import { useAppStore } from '../../stores/appStore';

interface StreamingStatus {
  step: number;
  message: string;
  icon: string;
}

interface MessageListProps {
  messages: ChatMessage[];
  isLoading?: boolean;
  isStreaming?: boolean;
  streamingMessageId?: string | null;
  streamingContent?: string;
  streamingStatus?: StreamingStatus | null;
  onSampleQuestionClick?: (question: string) => void;
}

// Code block component with copy functionality
const CodeBlock = ({ language, children }: { language: string; children: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group my-3">
      <div className="absolute right-2 top-2 z-10">
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-neutral-700 text-neutral-300 rounded hover:bg-neutral-600 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Copy code"
        >
          {copied ? (
            <>
              <Check className="w-3 h-3 text-green-400" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneDark}
        customStyle={{
          margin: 0,
          borderRadius: '0.5rem',
          fontSize: '0.8125rem',
          padding: '1rem',
        }}
        wrapLongLines
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
};

// Suggestion card component
interface SuggestionCardProps {
  item: SuggestionItem;
  variant: 'project' | 'recent' | 'trending' | 'personalized';
  onClick: () => void;
}

const SuggestionCard = ({ item, variant, onClick }: SuggestionCardProps) => {
  const variantStyles = {
    project: 'hover:border-amber-300 hover:bg-amber-50/50',
    recent: 'hover:border-blue-300 hover:bg-blue-50/50',
    trending: 'hover:border-orange-300 hover:bg-orange-50/50',
    personalized: 'hover:border-purple-300 hover:bg-purple-50/50',
  };

  return (
    <div
      className={`group p-3 border border-neutral-200 rounded-lg cursor-pointer transition-all duration-200 ${variantStyles[variant]}`}
      onClick={onClick}
    >
      <p className="text-sm text-neutral-700 group-hover:text-neutral-900 flex items-start gap-2">
        <span className="flex-1">"{item.question}"</span>
        <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
      </p>
      {item.sourceHint && (
        <p className="text-xs text-neutral-500 mt-1.5">{item.sourceHint}</p>
      )}
    </div>
  );
};

export default function MessageList({
  messages,
  isLoading,
  isStreaming,
  streamingMessageId,
  streamingContent,
  streamingStatus,
  onSampleQuestionClick
}: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [expandedInfoId, setExpandedInfoId] = useState<string | null>(null);

  // Get user and case info for suggestions
  const currentUser = useAppStore((state) => state.currentUser);
  const activeCase = useAppStore((state) => state.activeCase);

  // Personalized suggestions state
  const [suggestions, setSuggestions] = useState<SuggestionsResponse | null>(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);

  // Fetch personalized suggestions when there are no messages
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (messages.length > 0 || !currentUser?.id) return;

      setSuggestionsLoading(true);
      setSuggestionsError(null);

      try {
        const response = await aiAssistantAPI.getSuggestions(
          currentUser.id,
          activeCase?.id,
          3
        );
        setSuggestions(response);
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        setSuggestionsError('Failed to load suggestions');
      } finally {
        setSuggestionsLoading(false);
      }
    };

    fetchSuggestions();
  }, [currentUser?.id, activeCase?.id, messages.length]);

  // Custom markdown components for enhanced rendering
  const markdownComponents = useMemo(() => ({
    // Code blocks with syntax highlighting
    code: ({ node, inline, className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      const codeString = String(children).replace(/\n$/, '');

      if (!inline && (language || codeString.includes('\n'))) {
        return <CodeBlock language={language}>{codeString}</CodeBlock>;
      }
      // Inline code
      return (
        <code
          className="px-1.5 py-0.5 bg-neutral-200 text-neutral-800 rounded text-sm font-mono"
          {...props}
        >
          {children}
        </code>
      );
    },
    // Tables with better styling
    table: ({ children }: any) => (
      <div className="overflow-x-auto my-3 rounded-lg border border-neutral-200">
        <table className="min-w-full divide-y divide-neutral-200">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }: any) => (
      <thead className="bg-neutral-50">{children}</thead>
    ),
    th: ({ children }: any) => (
      <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
        {children}
      </th>
    ),
    td: ({ children }: any) => (
      <td className="px-3 py-2 text-sm text-neutral-700 border-t border-neutral-100">
        {children}
      </td>
    ),
    tr: ({ children }: any) => (
      <tr className="hover:bg-neutral-50 transition-colors">{children}</tr>
    ),
    // Links
    a: ({ href, children }: any) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary-600 hover:text-primary-700 underline"
      >
        {children}
      </a>
    ),
    // Blockquotes
    blockquote: ({ children }: any) => (
      <blockquote className="border-l-4 border-primary-300 pl-4 py-1 my-3 bg-primary-50/50 italic text-neutral-700">
        {children}
      </blockquote>
    ),
  }), []);

  // Auto-scroll to bottom when new messages arrive or streaming content updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, streamingContent]);

  // Helper to get display content for a message (streaming or static)
  const getMessageContent = (message: ChatMessage): string => {
    if (isStreaming && message.messageId === streamingMessageId) {
      return streamingContent || '';
    }
    return message.content;
  };

  // Check if message is currently streaming
  const isMessageStreaming = (message: ChatMessage): boolean => {
    return isStreaming === true && message.messageId === streamingMessageId;
  };

  const toggleInfo = (messageId: string) => {
    setExpandedInfoId(expandedInfoId === messageId ? null : messageId);
  };

  const formatTime = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'h:mm a');
    } catch {
      return '';
    }
  };

  const getConfidenceColor = (level?: string) => {
    switch (level) {
      case 'High':
        return 'text-green-600';
      case 'Medium':
        return 'text-amber-600';
      case 'Low':
        return 'text-red-600';
      default:
        return 'text-neutral-600';
    }
  };

  // Get response format display info
  const getResponseFormatInfo = (format?: ResponseFormatType) => {
    switch (format) {
      case 'mbb_pyramid':
        return {
          label: 'Strategic Analysis',
          icon: BarChart3,
          color: 'text-purple-600 bg-purple-50',
          description: 'MBB Pyramid Principle structure'
        };
      case 'conversational':
        return {
          label: 'Conversational',
          icon: MessageCircle,
          color: 'text-blue-600 bg-blue-50',
          description: 'Natural, friendly response'
        };
      case 'structured':
        return {
          label: 'Structured',
          icon: List,
          color: 'text-teal-600 bg-teal-50',
          description: 'Organized lists and comparisons'
        };
      case 'technical':
        return {
          label: 'Technical',
          icon: Code,
          color: 'text-orange-600 bg-orange-50',
          description: 'Code examples and implementation details'
        };
      default:
        return {
          label: 'Response',
          icon: Sparkles,
          color: 'text-neutral-600 bg-neutral-50',
          description: 'Standard response'
        };
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {messages.length === 0 && !isLoading && (
        <div className="flex flex-col items-center justify-center h-full text-center px-4">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Welcome to IntelliKnow™ Assistant
          </h3>
          <p className="text-neutral-600 max-w-md mb-6">
            Ask questions about companies, enterprise systems, or business domains. I can provide
            insights backed by our knowledge graph.
          </p>

          {/* Loading state */}
          {suggestionsLoading && (
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading personalized suggestions...</span>
            </div>
          )}

          {/* Error fallback to default suggestions */}
          {suggestionsError && (
            <div className="grid grid-cols-1 gap-3 w-full max-w-lg text-left">
              {['What is SAP S/4HANA?', 'Tell me about Apple Inc', 'Compare Oracle and SAP ERP systems'].map((question) => (
                <div
                  key={question}
                  className="p-3 border border-neutral-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 cursor-pointer transition-colors"
                  onClick={() => onSampleQuestionClick?.(question)}
                >
                  <p className="text-sm text-neutral-700">"{question}"</p>
                </div>
              ))}
            </div>
          )}

          {/* Personalized suggestions */}
          {suggestions && !suggestionsLoading && (
            <div className="w-full max-w-2xl space-y-6">
              {/* Project-specific suggestions (if available) */}
              {suggestions.project_specific && suggestions.project_specific.length > 0 && (
                <div className="text-left">
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                      For Your Project
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {suggestions.project_specific.map((item, idx) => (
                      <SuggestionCard
                        key={`project-${idx}`}
                        item={item}
                        variant="project"
                        onClick={() => onSampleQuestionClick?.(item.question)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Recent topics (continue exploring) */}
              {suggestions.recent_topics && suggestions.recent_topics.length > 0 && (
                <div className="text-left">
                  <div className="flex items-center gap-2 mb-3">
                    <History className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                      Continue Exploring
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {suggestions.recent_topics.map((item, idx) => (
                      <SuggestionCard
                        key={`recent-${idx}`}
                        item={item}
                        variant="recent"
                        onClick={() => onSampleQuestionClick?.(item.question)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Trending questions */}
              {suggestions.trending && suggestions.trending.length > 0 && (
                <div className="text-left">
                  <div className="flex items-center gap-2 mb-3">
                    <Flame className="w-4 h-4 text-orange-600" />
                    <span className="text-xs font-semibold text-orange-700 uppercase tracking-wide">
                      Trending Questions
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {suggestions.trending.map((item, idx) => (
                      <SuggestionCard
                        key={`trending-${idx}`}
                        item={item}
                        variant="trending"
                        onClick={() => onSampleQuestionClick?.(item.question)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Personalized suggestions */}
              {suggestions.personalized && suggestions.personalized.length > 0 && (
                <div className="text-left">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-4 h-4 text-purple-600" />
                    <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">
                      You Might Like
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {suggestions.personalized.map((item, idx) => (
                      <SuggestionCard
                        key={`personalized-${idx}`}
                        item={item}
                        variant="personalized"
                        onClick={() => onSampleQuestionClick?.(item.question)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {messages.map((message) => (
        <div
          key={message.messageId}
          className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}
        >
          {message.role === 'assistant' && (
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-primary-600" />
            </div>
          )}

          <div
            className={`flex flex-col max-w-3xl ${
              message.role === 'user' ? 'items-end' : 'items-start'
            }`}
          >
            {/* Message bubble */}
            <div
              className={`rounded-lg p-4 ${
                message.role === 'user'
                  ? 'bg-primary-600 text-white'
                  : 'bg-neutral-100 text-neutral-900'
              }`}
            >
              {message.role === 'user' ? (
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              ) : (
                <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-sm prose-headings:text-neutral-800 prose-headings:mb-2 prose-headings:mt-3 prose-p:text-neutral-700 prose-p:leading-relaxed prose-li:text-neutral-700 prose-li:leading-relaxed prose-strong:text-neutral-900 prose-strong:font-semibold prose-ul:my-2 prose-ol:my-2">
                  <ReactMarkdown components={markdownComponents}>
                    {getMessageContent(message)}
                  </ReactMarkdown>
                  {/* Blinking cursor for streaming */}
                  {isMessageStreaming(message) && (
                    <span className="inline-block w-2 h-4 bg-primary-600 ml-0.5 animate-pulse" />
                  )}
                </div>
              )}
            </div>

            {/* Metadata - hide while streaming */}
            {!isMessageStreaming(message) && (
              <div className="flex items-center gap-3 mt-2 text-xs text-neutral-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTime(message.timestamp)}
                </span>

                {message.role === 'assistant' && message.executionTimeMs && (
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {(message.executionTimeMs / 1000).toFixed(1)}s
                  </span>
                )}

                {message.role === 'assistant' && message.confidence && (
                  <span className={`flex items-center gap-1 ${getConfidenceColor(message.confidence.overall_level)}`}>
                    Confidence: {message.confidence.overall_level} ({Math.round(message.confidence.overall_score * 100)}%)
                  </span>
                )}

                {/* Response Format Badge */}
                {message.role === 'assistant' && message.responseFormat && (
                  (() => {
                    const formatInfo = getResponseFormatInfo(message.responseFormat);
                    const FormatIcon = formatInfo.icon;
                    return (
                      <span
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${formatInfo.color}`}
                        title={formatInfo.description}
                      >
                        <FormatIcon className="w-3 h-3" />
                        {formatInfo.label}
                      </span>
                    );
                  })()
                )}
              </div>
            )}

            {/* Streaming indicator with status */}
            {isMessageStreaming(message) && (
              <div className="flex items-center gap-2 mt-2 text-xs text-primary-600">
                {streamingStatus ? (
                  <>
                    {streamingStatus.icon === 'brain' && <Brain className="w-3 h-3 animate-pulse" />}
                    {streamingStatus.icon === 'search' && <Search className="w-3 h-3 animate-pulse" />}
                    {streamingStatus.icon === 'chart' && <LineChart className="w-3 h-3 animate-pulse" />}
                    {streamingStatus.icon === 'sparkles' && <Wand2 className="w-3 h-3 animate-pulse" />}
                    {!['brain', 'search', 'chart', 'sparkles'].includes(streamingStatus.icon) && (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    )}
                    <span>{streamingStatus.message}</span>
                  </>
                ) : (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Generating response...</span>
                  </>
                )}
              </div>
            )}

            {/* Sources */}
            {message.role === 'assistant' && message.orchestrationDetails && message.orchestrationDetails.sourcesSucceeded.length > 0 && (
              <div className="mt-2 text-xs text-neutral-500">
                <span className="font-medium">Sources:</span>{' '}
                {message.orchestrationDetails.sourcesSucceeded.map(source => {
                  if (source === 'neo4j') return 'Amplinth IntelliKnow';
                  if (source === 'web') return 'Web Search';
                  if (source === 'llm') return 'LLM Knowledge Base';
                  return source;
                }).join(', ')}
              </div>
            )}

            {/* Actions */}
            {message.role === 'assistant' && (
              <div className="mt-2 flex items-center gap-3">
                <MessageFeedback messageId={message.messageId} currentFeedback={message.feedback} />

                {/* Info icon to show reasoning */}
                <button
                  onClick={() => toggleInfo(message.messageId)}
                  className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-700 transition-colors"
                  title="Show how AI arrived at this answer"
                >
                  <Info className="w-3.5 h-3.5" />
                  <span>Info</span>
                  {expandedInfoId === message.messageId ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </button>
              </div>
            )}

            {/* Expandable info section - Purple Theme */}
            {message.role === 'assistant' && expandedInfoId === message.messageId && (
              <div className="mt-3 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg text-xs space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <p className="text-sm font-bold text-purple-900 uppercase tracking-wide">
                    AI Thought Process & Plan
                  </p>
                </div>

                {/* Query Classification Section */}
                {message.queryClassification && (
                  <div className="space-y-2 pb-3 border-b border-purple-200">
                    <div className="flex items-center gap-2">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        1
                      </div>
                      <div className="font-semibold text-purple-900">Query Classification</div>
                    </div>

                    <div className="pl-8 space-y-1.5">
                      <div className="flex gap-2">
                        <span className="text-purple-700 font-semibold min-w-[100px]">Query Type:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          message.queryClassification.intent === 'analytical_query'
                            ? 'bg-purple-200 text-purple-800'
                            : message.queryClassification.intent === 'data_query'
                            ? 'bg-blue-200 text-blue-800'
                            : 'bg-teal-200 text-teal-800'
                        }`}>
                          {message.queryClassification.intent.replace('_', ' ')}
                        </span>
                      </div>

                      <div className="flex gap-2">
                        <span className="text-purple-700 font-semibold min-w-[100px]">Complexity:</span>
                        <span className="text-purple-900 capitalize">{message.queryClassification.complexity}</span>
                      </div>

                      {message.queryClassification.responseFormat && (
                        <div className="flex gap-2">
                          <span className="text-purple-700 font-semibold min-w-[100px]">Format:</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getResponseFormatInfo(message.queryClassification.responseFormat).color}`}>
                            {getResponseFormatInfo(message.queryClassification.responseFormat).label}
                          </span>
                        </div>
                      )}

                      {message.queryClassification.contexts && message.queryClassification.contexts.length > 0 && (
                        <div className="flex gap-2">
                          <span className="text-purple-700 font-semibold min-w-[100px]">Context:</span>
                          <span className="text-purple-900 capitalize">{message.queryClassification.contexts.join(', ')}</span>
                        </div>
                      )}

                      {message.queryClassification.reasoning && (
                        <div className="mt-2">
                          <div className="text-purple-700 font-semibold">Reasoning:</div>
                          <div className="text-purple-800 mt-1 italic">{message.queryClassification.reasoning}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Orchestration Section */}
                {message.orchestrationDetails && (
                  <div className="space-y-2 pb-3 border-b border-purple-200">
                    <div className="flex items-center gap-2">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        2
                      </div>
                      <div className="font-semibold text-purple-900">Data Orchestration</div>
                    </div>

                    <div className="pl-8 space-y-1.5">
                      <div className="flex gap-2">
                        <span className="text-purple-700 font-semibold min-w-[100px]">Execution:</span>
                        <span className="text-purple-900">{message.orchestrationDetails.parallelExecution ? 'Parallel (faster)' : 'Sequential'}</span>
                      </div>

                      {message.orchestrationDetails.totalDurationMs && (
                        <div className="flex gap-2">
                          <span className="text-purple-700 font-semibold min-w-[100px]">Duration:</span>
                          <span className="text-purple-900">{message.orchestrationDetails.totalDurationMs}ms</span>
                        </div>
                      )}

                      {message.orchestrationDetails.sourcesSucceeded.length > 0 && (
                        <div className="space-y-1 mt-2">
                          <div className="text-purple-700 font-semibold">✓ Sources Queried:</div>
                          <ul className="list-none pl-4 text-purple-800 space-y-0.5">
                            {message.orchestrationDetails.sourcesSucceeded.map((source, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <span className="text-green-600 font-bold">✓</span>
                                <span>
                                  {source === 'neo4j' && 'Amplinth IntelliKnow (Graph Database)'}
                                  {source === 'web' && 'Web Search (Real-time data)'}
                                  {source === 'llm' && 'LLM Knowledge Base (General knowledge)'}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {message.orchestrationDetails.sourcesFailed && message.orchestrationDetails.sourcesFailed.length > 0 && (
                        <div className="space-y-1 mt-2">
                          <div className="text-red-600 font-semibold">✗ Failed Sources:</div>
                          <ul className="list-none pl-4 text-red-700 space-y-0.5">
                            {message.orchestrationDetails.sourcesFailed.map((source, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <span className="font-bold">✗</span>
                                <span>{source}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {message.orchestrationDetails.neo4jQuery && (
                        <div className="mt-2">
                          <div className="text-purple-700 font-semibold mb-1">Neo4j Cypher Query:</div>
                          <pre className="p-2 bg-white rounded border border-purple-300 text-xs overflow-x-auto text-purple-900">
                            {message.orchestrationDetails.neo4jQuery}
                          </pre>
                        </div>
                      )}

                      {message.orchestrationDetails.webSearchQuery && (
                        <div className="mt-2 flex gap-2">
                          <span className="text-purple-700 font-semibold">Web Search:</span>
                          <span className="text-purple-800 italic">"{message.orchestrationDetails.webSearchQuery}"</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Response Generation Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0 w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      3
                    </div>
                    <div className="font-semibold text-purple-900">Response Generation</div>
                  </div>

                  <div className="pl-8 space-y-1.5">
                    {message.responseFormat && (
                      <div className="flex gap-2">
                        <span className="text-purple-700 font-semibold min-w-[100px]">Format:</span>
                        <span className="text-purple-900">{message.responseFormat === 'mbb_pyramid' ? 'MBB Pyramid Structure' : message.responseFormat}</span>
                      </div>
                    )}

                    {message.confidence && (
                      <div className="flex gap-2">
                        <span className="text-purple-700 font-semibold min-w-[100px]">Confidence:</span>
                        <span className={`font-semibold ${getConfidenceColor(message.confidence.overall_level)}`}>
                          {message.confidence.overall_level} ({Math.round(message.confidence.overall_score * 100)}%)
                        </span>
                      </div>
                    )}

                    {message.executionTimeMs && (
                      <div className="flex gap-2">
                        <span className="text-purple-700 font-semibold min-w-[100px]">Total Time:</span>
                        <span className="text-purple-900">{(message.executionTimeMs / 1000).toFixed(1)} seconds</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Follow-up Questions - Show only for assistant messages that have them */}
            {message.role === 'assistant' && message.followUpQuestions && message.followUpQuestions.length > 0 && !isMessageStreaming(message) && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                  Suggested follow-ups
                </p>
                <div className="flex flex-wrap gap-2">
                  {message.followUpQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => onSampleQuestionClick?.(question)}
                      className="group flex items-center gap-1.5 px-3 py-1.5 text-sm text-neutral-700 bg-white border border-neutral-200 rounded-full hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 transition-all duration-200"
                    >
                      <span>{question}</span>
                      <ArrowRight className="w-3 h-3 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {message.role === 'user' && (
            <div className="w-8 h-8 bg-neutral-700 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-white" />
            </div>
          )}
        </div>
      ))}

      {/* Loading indicator - only show when loading but NOT streaming (streaming shows inline) */}
      {isLoading && !isStreaming && (
        <div className="flex gap-3">
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-primary-600" />
          </div>

          <div className="bg-neutral-100 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
              <span className="text-sm text-neutral-700">Thinking longer for better response...</span>
            </div>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
}
